import type { Action as ProcessAction } from '../../../../../bridge/types/action.ts';

/** Find the ask an action panel points at.
 *
 * Actions are not fetched into the panel the way artifacts are — there is no
 * getAction on the bridge — so read it from the process the user is already
 * looking at, or from its setup snapshot, both of which the reducer keeps in
 * lockstep with every edit.
 */
export const ownerActionFromPanel = (
	userState: unknown,
	panel: { item_id: string },
): ProcessAction | null => {
	const state = (userState ?? {}) as {
		chat_panel?: { process?: { actions?: ProcessAction[] } };
		chat_panel_history?: Array<{
			process?: { actions?: ProcessAction[] };
		}>;
		process_setup_snapshots?: Record<
			string,
			{ actions?: ProcessAction[] } | undefined
		>;
	};
	const fromPanel = (state.chat_panel?.process?.actions ?? []).find(
		(a) => a.id === panel.item_id,
	);
	if (fromPanel) return fromPanel;
	// The process panel that carried this action was pushed to history when
	// the action panel took its place. Search most-recent-first so the
	// freshest copy wins (mirrors the process_schedule lookup in ChatPanel).
	const history = state.chat_panel_history ?? [];
	for (let i = history.length - 1; i >= 0; i--) {
		const found = (history[i]?.process?.actions ?? []).find(
			(a) => a.id === panel.item_id,
		);
		if (found) return found;
	}
	for (const snapshot of Object.values(state.process_setup_snapshots ?? {})) {
		const found = (snapshot?.actions ?? []).find((a) => a.id === panel.item_id);
		if (found) return found;
	}
	return null;
};

/** Where this ask sits in the campaign: its name, and which of the owner's parts this is.
 *
 * Read from the setup snapshot only. An ask renders under an `ActionChatPanel`, which carries no
 * `process`, so the open panel cannot supply either value.
 *
 * The position counts the owner's OWN parts, never the campaign's steps: a campaign also holds
 * steps Bridgeway runs, so counting steps would tell the owner their campaign has fewer parts
 * than it does. A skipped part keeps its place, because this states a position and not progress.
 */
export const ownerAskContext = (
	userState: unknown,
	action: { id: string; process_id?: string },
): { campaignTitle: string | null; position: number; total: number } | null => {
	if (!action.process_id) return null;
	const state = (userState ?? {}) as {
		process_setup_snapshots?: Record<
			string,
			| { title?: string; actions?: Array<{ id: string; type: string }> }
			| undefined
		>;
	};
	const snapshot = state.process_setup_snapshots?.[action.process_id];
	if (!snapshot) return null;
	const asks = (snapshot.actions ?? []).filter(
		(a) => a.type === 'request_owner_action',
	);
	const position = asks.findIndex((a) => a.id === action.id) + 1;
	const title = (snapshot.title ?? '').trim();
	return {
		// Never a stand-in noun. An invented name reads as the campaign's real one.
		campaignTitle: title || null,
		position,
		total: asks.length,
	};
};

/** The one line the panel shows under its title, or `undefined` to show nothing. */
export const ownerAskContextLine = (
	userState: unknown,
	action: { id: string; process_id?: string },
): string | undefined => {
	const context = ownerAskContext(userState, action);
	if (!context) return undefined;
	const { campaignTitle, position, total } = context;
	// One part carries no position worth stating.
	const place = total > 1 && position > 0 ? `Part ${position} of ${total}` : '';
	if (campaignTitle && place) return `${campaignTitle} · ${place}`;
	return campaignTitle || place || undefined;
};
